import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronsRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  useEquipmentTags,
  useEquipmentTagsTree,
  useEquipmentTagCounts,
  type EquipmentTag,
  type EquipmentTagTreeNode,
} from "@/hooks/useEquipmentTags";

interface EquipmentTagPickerProps {
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  placeholder?: string;
  /** "badges" shows full-path badges (default), "compact" shows count when >1 */
  displayMode?: "badges" | "compact";
  /** Show equipment count next to each tag in the picker */
  showEquipmentCount?: boolean;
}

interface TagTreeSelectorProps {
  tagTree: EquipmentTagTreeNode[];
  tags: EquipmentTag[];
  selectedTagIds: string[];
  onChange: (tagIds: string[]) => void;
  placeholder: string;
  emptyLabel?: string;
  excludedTagIds?: string[];
  singleSelect?: boolean;
  searchablePlaceholder?: string;
  renderRowAction?: (tag: EquipmentTagTreeNode) => React.ReactNode;
  renderFooter?: () => React.ReactNode;
  /** "badges" shows full-path badges (default), "compact" shows count when >1 */
  displayMode?: "badges" | "compact";
  /** Map of tag id → equipment count to display next to each tag */
  equipmentCountByTagId?: Record<string, number>;
}

interface VisibleTagRow {
  tag: EquipmentTagTreeNode;
  forceExpanded: boolean;
}

const getDefaultExpandedGroups = (tags: EquipmentTagTreeNode[]) => {
  const expanded = new Set<string>();

  const visit = (nodes: EquipmentTagTreeNode[]) => {
    for (const node of nodes) {
      if (node.children.length > 0) {
        expanded.add(node.id);
        visit(node.children);
      }
    }
  };

  visit(tags);
  return expanded;
};

const collectSelectedBranchIds = (
  node: EquipmentTagTreeNode,
  selectedTagIds: Set<string>,
  result: Set<string>,
): boolean => {
  let branchHasSelection = selectedTagIds.has(node.id);

  for (const child of node.children) {
    if (collectSelectedBranchIds(child, selectedTagIds, result)) {
      branchHasSelection = true;
      result.add(node.id);
    }
  }

  return branchHasSelection;
};

const buildVisibleRows = (
  nodes: EquipmentTagTreeNode[],
  expandedGroups: Set<string>,
  query: string,
): VisibleTagRow[] => {
  const normalizedQuery = query.trim().toLowerCase();
  const rows: VisibleTagRow[] = [];

  const matchesQuery = (node: EquipmentTagTreeNode, normalized: string) => {
    if (!normalized) return true;
    if (
      node.searchableText.includes(normalized) ||
      node.fullPath.toLowerCase().includes(normalized)
    ) {
      return true;
    }
    return node.children.some((child) => matchesQuery(child, normalized));
  };

  const visit = (node: EquipmentTagTreeNode): boolean => {
    const shouldShow = matchesQuery(node, normalizedQuery);
    if (!shouldShow) return false;

    const childMatches = node.children.some((child) =>
      matchesQuery(child, normalizedQuery),
    );
    const forceExpanded = normalizedQuery.length > 0 && childMatches;
    rows.push({ tag: node, forceExpanded });

    const isExpanded = forceExpanded || expandedGroups.has(node.id);
    if (isExpanded) {
      for (const child of node.children) {
        visit(child);
      }
    }

    return true;
  };

  for (const node of nodes) {
    visit(node);
  }

  return rows;
};

export const collectDescendantTagIds = (
  nodes: EquipmentTagTreeNode[],
  tagId: string,
): string[] => {
  const descendants: string[] = [];

  const visit = (node: EquipmentTagTreeNode): boolean => {
    if (node.id === tagId) {
      const collect = (current: EquipmentTagTreeNode) => {
        for (const child of current.children) {
          descendants.push(child.id);
          collect(child);
        }
      };

      collect(node);
      return true;
    }

    return node.children.some((child) => visit(child));
  };

  nodes.some((node) => visit(node));
  return descendants;
};

export const TagTreeSelector = ({
  tagTree,
  tags,
  selectedTagIds,
  onChange,
  placeholder,
  emptyLabel,
  excludedTagIds = [],
  singleSelect = false,
  searchablePlaceholder = "Search tags...",
  renderRowAction,
  renderFooter,
  displayMode = "badges",
  equipmentCountByTagId,
}: TagTreeSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedGroups((current) =>
      current.size > 0 ? current : getDefaultExpandedGroups(tagTree),
    );
  }, [tagTree]);

  useEffect(() => {
    const selectedSet = new Set(selectedTagIds);
    const requiredExpanded = new Set<string>();

    for (const root of tagTree) {
      collectSelectedBranchIds(root, selectedSet, requiredExpanded);
    }

    if (requiredExpanded.size === 0) return;

    setExpandedGroups((current) => {
      const next = new Set(current);
      let changed = false;

      for (const groupId of requiredExpanded) {
        if (!next.has(groupId)) {
          next.add(groupId);
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [selectedTagIds, tagTree]);

  const tagMap = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags],
  );
  const excludedIds = useMemo(() => new Set(excludedTagIds), [excludedTagIds]);

  const selectedTags = useMemo(
    () => tags.filter((tag) => selectedTagIds.includes(tag.id)),
    [selectedTagIds, tags],
  );

  const visibleRows = useMemo(
    () =>
      buildVisibleRows(tagTree, expandedGroups, search).filter(
        ({ tag }) => !excludedIds.has(tag.id),
      ),
    [excludedIds, expandedGroups, search, tagTree],
  );

  const getTagDisplayLabel = (tag: EquipmentTag) => {
    const names = [tag.name];
    let currentParentId = tag.parent_id;
    const visited = new Set<string>();

    while (currentParentId && !visited.has(currentParentId)) {
      visited.add(currentParentId);
      const parent = tagMap.get(currentParentId);
      if (!parent) break;
      names.unshift(parent.name);
      currentParentId = parent.parent_id;
    }

    return names.join(" > ");
  };

  const toggleTag = (tagId: string) => {
    if (singleSelect) {
      onChange(selectedTagIds.includes(tagId) ? [] : [tagId]);
      setOpen(false);
      return;
    }

    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
      return;
    }

    onChange([...selectedTagIds, tagId]);
  };

  const toggleGroup = (tagId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const removeTag = (tagId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const clearSelection = () => {
    onChange([]);
    setOpen(false);
  };

  const getRowValue = (tag: EquipmentTagTreeNode) =>
    `${tag.fullPath} ${tag.searchableText}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-10 w-full justify-between"
          data-testid="equipment-tag-picker-trigger"
        >
          <div className="flex flex-wrap gap-1 text-left">
            {selectedTags.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : displayMode === "compact" && selectedTags.length > 1 ? (
              <span>{selectedTags.length} tags</span>
            ) : (
              selectedTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="secondary"
                  className="mr-1 max-w-full"
                >
                  <span className="truncate">{getTagDisplayLabel(tag)}</span>
                  {!singleSelect ? (
                    <button
                      type="button"
                      className="ml-1 inline-flex shrink-0 pointer-events-auto"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => removeTag(tag.id, event)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </Badge>
              ))
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(28rem,var(--radix-popover-trigger-width))] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchablePlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No tags found.</CommandEmpty>
            {emptyLabel ? (
              <>
                <CommandGroup>
                  <CommandItem onSelect={clearSelection}>
                    {emptyLabel}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            ) : null}
            <CommandGroup>
              {visibleRows.map(({ tag, forceExpanded }) => {
                const isSelected = selectedTagIds.includes(tag.id);
                const isExpanded = forceExpanded || expandedGroups.has(tag.id);
                const hasChildren = tag.children.length > 0;

                return (
                  <CommandItem
                    key={tag.id}
                    value={getRowValue(tag)}
                    onSelect={() => toggleTag(tag.id)}
                    className="flex items-center justify-between gap-2"
                    data-testid={`equipment-tag-row-${tag.id}`}
                  >
                    <div
                      className="flex min-w-0 flex-1 items-center gap-2"
                      style={{ paddingLeft: `${tag.depth * 16}px` }}
                    >
                      {hasChildren ? (
                        <button
                          type="button"
                          aria-label={
                            isExpanded ? "Collapse group" : "Expand group"
                          }
                          className="flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent"
                          onClick={(event) => toggleGroup(tag.id, event)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : (
                        <span className="w-5 shrink-0" />
                      )}
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{tag.name}</span>
                      {equipmentCountByTagId !== undefined && (
                        <span className="ml-1 shrink-0 text-xs text-muted-foreground">
                          {equipmentCountByTagId[tag.id] ?? 0}
                        </span>
                      )}
                    </div>
                    {renderRowAction?.(tag)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {renderFooter ? (
              <>
                <CommandSeparator />
                <CommandGroup>{renderFooter()}</CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Selects tags for equipment. Tags are created, edited and deleted only on the
 * tag management page (/equipment/tags).
 */
export const EquipmentTagPicker = ({
  selectedTagIds,
  onChange,
  placeholder = "Select tags...",
  displayMode = "badges",
  showEquipmentCount = false,
}: EquipmentTagPickerProps) => {
  const { data: tags = [] } = useEquipmentTags();
  const { data: tagTree = [] } = useEquipmentTagsTree();
  const { data: tagCounts } = useEquipmentTagCounts();

  return (
    <TagTreeSelector
      tagTree={tagTree}
      tags={tags}
      selectedTagIds={selectedTagIds}
      onChange={onChange}
      placeholder={placeholder}
      displayMode={displayMode}
      equipmentCountByTagId={showEquipmentCount ? tagCounts : undefined}
    />
  );
};
